import React, { useState } from 'react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { mask, unMask } from 'remask';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { GiCog } from 'react-icons/gi';
import { FaCog, FaEye, FaTimes, FaPencilAlt, FaPlus } from 'react-icons/fa';

import AppCard from '../../../../components/appCard';
import { useInfo } from '../../../../context/infoContext';
import { ApiAdimplencia } from '../../../../services/api';
import { LargeTablePague, TRANSITIONS } from '../../../../styles/primeReactConfig';

import HeaderDeFiltrosContratante from '../../../../components/dashboard/adimplencias/contratantes/HeaderDeFiltrosContratante';
import ModaisContratante from '../../../../components/dashboard/adimplencias/contratantes/ModaisContratante';
import { useContratantes } from '../../../../components/dashboard/adimplencias/contratantes/hooks/useContratantes';
import { executarExportacaoDireta } from '../../../../components/dashboard/adimplencias/contratantes/exportar';
import useTerminologiaRecorrencia from '../../../../hooks/useTerminologiaRecorrencia.js';
import { isGeracaoBoletoSuspensa } from '../../../../utils/geracaoBoleto';

/**
 * Página de gestão de contratantes.
 *
 * @param {{ rowsPerPage?: number[] }} props
 * @returns {JSX.Element}
 */
export const Contratantes = ({ rowsPerPage }) => {
  const { credorData, gestorData, providerInfo } = useInfo();
  const terminologia = useTerminologiaRecorrencia();
  const geracaoSuspensa = isGeracaoBoletoSuspensa(credorData);

  const {
    menu,
    selectedFilter,
    setSelectedFilter,
    setReload,
    isLoading,
    first,
    setFirst,
    contractsData,
    totalContracts,
    filterParam,
    setFilterParam,
    setSearchState,
    grupoFilter,
    setGrupoFilter,
    gruposOptions,
    rowCurrent,
    setRowCurrent,
    page,
    rows,
    setCurrentPage,
    setRows,
    fetchContratantes,
    buildFilters,
    statusFilter,
    setStatusFilter,
  } = useContratantes({ gestorData, credorData });

  const [showModals, setShowModals] = useState({
    create: false,
    edit: false,
    lancamento: false,
    cancela: false,
    recebivel: false,
  });

  const [isExporting, setIsExporting] = useState(false);

  const [menuItems, setMenuItems] = useState([]);

  /**
   * Normaliza o status do contratante para boolean.
   *
   * @param {boolean|number|string|null|undefined} value
   * @returns {boolean}
   */
  function isContratoAtivo(value) {
    if (value === true || value === 1 || value === '1') {
      return true;
    }

    if (value === false || value === 0 || value === '0' || value == null) {
      return false;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();

      if (['true', 'ativo', 'active', 'sim', 'yes'].includes(normalized)) {
        return true;
      }

      if (['false', 'inativo', 'inactive', 'nao', 'não', 'no'].includes(normalized)) {
        return false;
      }
    }

    return false;
  }

  /**
   * Converte uma string base64 em Blob PDF.
   *
   * @param {string} data
   * @returns {Blob}
   */
  function base64toBlob(data) {
    const contentType = 'application/pdf';
    const base64WithoutPrefix = data.substr(`data:${contentType};base64,`.length);
    const bytes = atob(base64WithoutPrefix);
    const out = new Uint8Array(bytes.length);

    for (let i = 0; i < bytes.length; i += 1) {
      out[i] = bytes.charCodeAt(i);
    }

    return new Blob([out], { type: contentType });
  }

  /**
   * Abre o arquivo do contrato em nova aba.
   *
   * @param {string} contratanteId
   * @returns {void}
   */
  function abreContrato(contratanteId) {
    if (!contratanteId) {
      toast.error('Contrato inválido.');
      return;
    }

    toast.info('Abrindo contrato');

    ApiAdimplencia.get(`/contratante/download/${contratanteId}`)
        .then(({ data }) => {
          if (!data) {
            toast.error('Contrato indisponível.');
            return;
          }

          const base64 = `data:application/pdf;base64,${data}`;
          const url = URL.createObjectURL(base64toBlob(base64));
          window.open(url, '_blank', 'noopener,noreferrer');
        })
        .catch(() => {
          toast.error('Não foi possível abrir o contrato');
        });
  }

  /**
   * Inicia a exportação direta em XLS com todos os registros do resultado atual (respeitando filtros, sem modal).
   *
   * @returns {Promise<void>}
   */
  async function handleExportar() {
    setIsExporting(true);
    try {
      await executarExportacaoDireta(buildFilters(), terminologia);
    } finally {
      setIsExporting(false);
    }
  }

  /**
   * Executa a busca ao pressionar Enter.
   *
   * @param {number} keyCode
   * @returns {void}
   */
  function handleSearch(keyCode) {
    if (keyCode !== 13) {
      return;
    }

    let sanitized = filterParam;

    if (selectedFilter === 'usuario_documento') {
      sanitized = sanitized.replace(/[./-]/g, '');
    }

    if (
        selectedFilter !== 'usuario_nome' &&
        selectedFilter !== 'beneficiario' &&
        selectedFilter !== 'usuario_documento'
    ) {
      sanitized = sanitized.replace(/[^a-zA-Z0-9\s]/g, '');
    }

    if (sanitized.length === 0) {
      setSearchState(false);
      fetchContratantes(0, rows);
      return;
    }

    setSearchState(true);

    const filters = { [selectedFilter]: sanitized };

    if (grupoFilter) {
      filters.credor_grupo_id = grupoFilter;
    }

    if (statusFilter === 'ativos') {
      filters.ativo = true;
    } else if (statusFilter === 'inativos') {
      filters.ativo = false;
    }

    fetchContratantes(page, rows, filters);
  }

  /**
   * Trata a paginação da tabela.
   *
   * @param {{ rows: number, page: number, first: number }} event
   * @returns {void}
   */
  function onPage(event) {
    setRows(event.rows);
    setCurrentPage(event.page);
    setFirst(event.first);
    fetchContratantes(event.page, event.rows);
  }

  /**
   * Cria um evento mínimo compatível para abrir o popup do PrimeReact
   * a partir do botão clicado.
   *
   * @param {HTMLElement|null} buttonElement
   * @returns {{ currentTarget: HTMLElement|null, target: HTMLElement|null, preventDefault: Function, stopPropagation: Function }}
   */
  function buildMenuToggleEvent(buttonElement) {
    return {
      currentTarget: buttonElement,
      target: buttonElement,
      preventDefault: () => {},
      stopPropagation: () => {},
    };
  }

  /**
   * Retorna as ações disponíveis para um contratante.
   *
   * @param {object|null} currentRow
   * @returns {Array<object>}
   */
  function getAcoesContrato(currentRow) {
    if (!currentRow) {
      return [];
    }

    const contratoAtivo = isContratoAtivo(currentRow?.contratante_status);

    return [
      {
        label: geracaoSuspensa ? 'Novo Parcelamento (geração suspensa)' : 'Novo Parcelamento',
        icon: <FaCog className="mr-2" />,
        disabled: geracaoSuspensa,
        command: () => {
          setRowCurrent(currentRow);
          setShowModals((prev) => ({ ...prev, recebivel: true }));
        },
      },
      {
        label: 'Editar Cliente',
        icon: <FaPencilAlt className="mr-2" />,
        command: () => {
          setRowCurrent(currentRow);
          setShowModals((prev) => ({ ...prev, edit: true }));
        },
      },
      {
        label: geracaoSuspensa ? 'Boleto Avulso (geração suspensa)' : 'Boleto Avulso',
        icon: <FaPlus className="mr-2" />,
        disabled: geracaoSuspensa,
        command: () => {
          setRowCurrent(currentRow);
          setShowModals((prev) => ({ ...prev, lancamento: true }));
        },
      },
      {
        label: contratoAtivo ? 'Desativar contrato' : 'Reativar Contrato',
        icon: contratoAtivo ? <FaTimes className="mr-2" /> : <FaEye className="mr-2" />,
        command: () => {
          setRowCurrent(currentRow);
          setShowModals((prev) => ({ ...prev, cancela: true }));
        },
      },
      {
        label: currentRow?.contrante_arquivo ? 'Abrir contrato' : 'Arquivo indisponível',
        icon: currentRow?.contrante_arquivo ? <FaEye className="mr-2" /> : <FaTimes className="mr-2" />,
        disabled: !currentRow?.contrante_arquivo,
        command: () => {
          setRowCurrent(currentRow);
          abreContrato(currentRow?.contratante_id);
        },
      },
    ];
  }

  /**
   * Abre o menu de ações da linha clicada garantindo que o model
   * e a linha atual estejam sincronizados.
   *
   * @param {React.MouseEvent<HTMLElement>} event
   * @param {object} currentRow
   * @returns {void}
   */
  function handleOpenActionsMenu(event, currentRow) {
    const triggerElement = event.currentTarget;

    setRowCurrent(currentRow);
    setMenuItems(getAcoesContrato(currentRow));

    window.requestAnimationFrame(() => {
      if (menu.current) {
        menu.current.toggle(buildMenuToggleEvent(triggerElement));
      }
    });
  }

  /**
   * Renderiza o botão de ações da linha.
   *
   * @param {object} rowData
   * @returns {JSX.Element}
   */
  const actionTemplate = (rowData) => (
      <Button
          icon={<GiCog className="text-2xl" />}
          onClick={(event) => handleOpenActionsMenu(event, rowData)}
          pt={{
            root: `hover:dark:bg-emerald-300 hover:bg-green-600 
          dark:bg-emerald-500 bg-green-500
          h-10 w-10 rounded-full
          flex justify-center items-center
          text-white dark:text-gray-900 
          transition duration-300`,
            label: 'absolute',
          }}
          rounded
      />
  );

  /**
   * Renderiza o badge de status do contratante.
   *
   * @param {object} row
   * @returns {JSX.Element}
   */
  const statusTemplate = (row) => {
    const ativo = isContratoAtivo(row?.contratante_status);

    return (
        <span
            className={classNames(
                'text-nowrap py-1 px-2 justify-center flex w-auto',
                ativo ? 'bg-sky-200 text-sky-600' : 'bg-red-300 text-red-700'
            )}
        >
        <p className="uppercase text-sm font-bold">
          {ativo ? 'Ativo' : 'Inativo'}
        </p>
      </span>
    );
  };

  return (
      <AppCard className="!p-0">
        <ModaisContratante
            showModals={showModals}
            setShowModals={setShowModals}
            reload={() => setReload((prev) => !prev)}
            rowCurrent={rowCurrent}
            providerInfo={providerInfo}
            credorId={credorData?.credor_id}
            totalContracts={totalContracts}
            credorGrupoId={grupoFilter}
        />

        <Menu
            model={menuItems}
            ref={menu}
            popup
            pt={{
              root: 'py-1 bg-white dark:bg-gray-900 text-gray-700 dark:text-white/80 border border-gray-300 dark:border-blue-900/40 rounded-md w-60',
              menu: { className: 'm-0 p-0 list-none' },
              content: ({ context }) => ({
                className: classNames(
                    'text-gray-700 dark:text-white/80 transition-shadow duration-200 rounded-none',
                    {
                      'hover:text-gray-700 dark:hover:text-white/80 hover:bg-gray-200 dark:hover:bg-gray-800/80':
                          !context.item.disabled,
                      'cursor-default pointer-events-none select-none opacity-60':
                      context.item.disabled,
                    }
                ),
              }),
              action: {
                className: 'text-gray-700 dark:text-white/80 py-2 px-3 select-none cursor-pointer flex items-center',
              },
              separator: 'border-t border-gray-300 dark:border-blue-900/40 my-1',
              transition: TRANSITIONS.overlay,
            }}
        />

        <DataTable
            value={contractsData}
            header={(
                <HeaderDeFiltrosContratante
                    credorData={credorData}
                    selectedFilter={selectedFilter}
                    setSelectedFilter={setSelectedFilter}
                    filterParam={filterParam}
                    setFilterParam={setFilterParam}
                    handleSearch={handleSearch}
                    setReload={setReload}
                    setSearchState={setSearchState}
                    setShowModals={setShowModals}
                    contractsData={contractsData}
                    grupoFilter={grupoFilter}
                    setGrupoFilter={setGrupoFilter}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    gruposOptions={gruposOptions}
                    onExportar={handleExportar}
                    isExporting={isExporting}
                />
            )}
            rows={rows}
            rowsPerPageOptions={rowsPerPage || [10, 20, 50]}
            totalRecords={totalContracts}
            first={first}
            onPage={onPage}
            loading={isLoading}
            paginator
            lazy
            scrollable
            emptyMessage="Nenhum contrato encontrado."
            currentPageReportTemplate="Exibindo {first} de {last} do total de {totalRecords}"
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            pt={LargeTablePague}
        >
          <Column header="Ações" body={actionTemplate} style={{ minWidth: '5rem' }} />
          <Column field="contratante_numero" header="Nº Cliente" />
          <Column field="grupo_descricao" header={terminologia.labelGrupoNoContratante} />
          <Column field="usuario_nome" header="Cliente" />
          <Column
              field="usuario_documento"
              header="Documento"
              body={({ usuario_documento }) =>
                  mask(unMask(usuario_documento), ['999.999.999-99', '99.999.999/9999-99'])
              }
              style={{ minWidth: '10rem', fontSize: '90%' }}
          />
          <Column field="beneficiario" header="Beneficiário" />
          <Column header="Status" body={statusTemplate} style={{ textAlign: 'center' }} />
        </DataTable>
      </AppCard>
  );
};